/**
 * Admin simulations API — the catalog now lives in Cosmos (served by the backend),
 * not a hardcoded registry. The HTML assets are on the sims Cloudflare Pages project;
 * `assetUrl`/`thumbnailUrl` are absolute URLs there.
 * See docs/FeatureDesigns/simulations/student-simulations.md.
 */
import { fetchWithAdmin } from './fetchHelper';
import type { SubjectType } from '@padcanvas/shared';
import { experimentListing, type SimulationListing, type SimulationsSource } from '@shared-ui/simulations';
import { questionApi } from './questionApi';
import { chemistryLabApi, type GuidedExperimentAdmin } from './chemistryLabApi';

export type SimStatus = 'live' | 'draft';

/** What admin may change on a simulation or a guided experiment — and all it
 *  may change. The rest is mastered in the repo catalogue. */
export interface CurationChanges {
  status?: SimStatus;
  moduleIds?: string[];
  boardGrades?: string[];
}

export interface Simulation {
  id: string;
  title: string;
  tagline: string;
  description: string;
  /** Canonical subject (shared SUBJECT_OPTIONS enum). */
  subject: SubjectType;
  topic: string;
  parameters: string[];
  moduleIds: string[];
  /** Flat board-grade keys ('cbse:10', 'jee'), as questions store them. */
  boardGrades?: string[];
  /** Absent means boardGrades is still the seed's proposal, not a decision. */
  boardGradesCuratedAt?: string;
  assetUrl: string;
  /** Which contract the asset is built to. `2` declares a manifest and is
   *  rendered by the shared SimulationPlayer; absent or `1` is a legacy page
   *  embedded as-is. */
  runtime?: 1 | 2;
  thumbnailUrl?: string;
  glyph: string;
  /** Narration voices this explainer offers (explainers only; may be absent). */
  voices?: Array<{ key: string; label: string; lang: string; register: string }>;
  status: SimStatus;
  /** When the row last changed, from Cosmos `_ts`. Admin-only — the
   *  student-facing endpoint does not send it. Absent on a doc never written
   *  through the store (shouldn't happen, but the column tolerates it). */
  updatedAt?: string;
}

export const simulationsApi = {
  /** All sims (incl. drafts) — admin gallery. */
  list: () =>
    fetchWithAdmin<{ simulations: Simulation[] }>('/console/simulations').then((r) => r.simulations),

  /** One sim by id, from the full list (so drafts resolve too). */
  get: async (id: string): Promise<Simulation | undefined> =>
    (await simulationsApi.list()).find((s) => s.id === id),

  /** Status, chapters and audience — all admin changes on a simulation. */
  curate: (id: string, changes: CurationChanges) =>
    fetchWithAdmin<{ simulation: Simulation }>(`/console/simulations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    }).then((r) => r.simulation),
};

/** What the shared gallery and detail page read in admin: every simulation,
 *  drafts included, and chapter names across inactive modules too. */
export const adminSimulationsSource: SimulationsSource<Simulation> = {
  list: simulationsApi.list,
  get: simulationsApi.get,
  moduleNames: (subject) =>
    questionApi.getModules({ subject, full: true }).then((r) => r.modules ?? []),
};

/**
 * A guided experiment as a row of the simulations list, which lists both kinds
 * as zitmus does. The topic says which kind a row is; the thumbnail is the
 * experiment's screenshot, published beside the simulations' own, or its
 * equation when it has none.
 */
export type CatalogueRow = Simulation
  & Pick<SimulationListing, 'equation' | 'accentColor' | 'detail'>
  & { kind: 'simulation' | 'experiment' };

function experimentRow(e: GuidedExperimentAdmin, thumbBase: string | undefined): CatalogueRow {
  return {
    ...experimentListing(e, thumbBase && e.thumb ? `${thumbBase}${e.thumb.replace(/^thumbs\//, '')}` : undefined),
    kind: 'experiment',
    boardGrades: e.boardGrades,
    boardGradesCuratedAt: e.boardGradesCuratedAt,
    status: e.status,
  };
}

/** Simulations, and — for someone who may curate them — the guided
 *  experiments beside them. Only a simulation opens in the player, so the
 *  single-row lookup and chapter names stay the simulations'. */
export function adminCatalogueSource(withExperiments: boolean): SimulationsSource<CatalogueRow> {
  return {
    list: async () => {
      const [sims, experiments] = await Promise.all([
        simulationsApi.list(),
        withExperiments ? chemistryLabApi.listExperiments() : Promise.resolve([]),
      ]);
      // The folder the simulations' screenshots are served from, where the
      // experiments' are published too.
      const thumbBase = sims.find(s => s.thumbnailUrl)?.thumbnailUrl?.replace(/[^/]+$/, '');
      return [
        ...sims.map((s): CatalogueRow => ({ ...s, kind: 'simulation' })),
        ...experiments.map(e => experimentRow(e, thumbBase)),
      ];
    },
    get: async (id) => {
      const sim = await simulationsApi.get(id);
      return sim ? { ...sim, kind: 'simulation' } : undefined;
    },
    moduleNames: adminSimulationsSource.moduleNames,
  };
}
